/**
 * @fileoverview Provide the day-grouped agenda for caller-supplied calendar records. `kc-internal-frontend/src/components/Calendar/BespokeCalendar.js` owns navigation; shared calendar utilities and EventComponent own time/layout conventions and action signifiers. The view delegates event/day selection without owning event persistence or authorization.
 */

import React, { useMemo, useRef, useEffect } from 'react';
import moment from 'moment-timezone';
import EventComponent from '../components/EventComponent';
import {
  generateMonthGrid,
  groupEventsByDay,
  isToday,
  CALENDAR_VIEWS,
  formatTime,
  formatDate } from
'../utils/calendarUtils';
import AppIcon from "../../shared/AppIcon";



const AgendaView = ({
  currentDate,
  events = [],
  onEventClick,
  onEventEdit,
  onEventDelete,
  onConfirmResponsibility,
  assignees = [],
  currentUser = null
}) => {





  const todayRef = useRef(null);

  // Generate the month range for the agenda
  const monthGrid = useMemo(() => generateMonthGrid(currentDate), [currentDate]);

  // Group events by day for the agenda view
  const agendaData = useMemo(

  () => {
    const startDate = monthGrid[0][0];
    const endDate = monthGrid[5][6];
    const eventsByDay = groupEventsByDay(events, startDate, endDate);

    // Create agenda entries for each day that has events
    const agendaEntries = [];
    let currentMoment = moment(startDate);

    while (currentMoment.isSameOrBefore(endDate)) {
      const dateKey = currentMoment.format('YYYY-MM-DD');
      const dayEvents = eventsByDay[dateKey] || [];

      if (dayEvents.length > 0) {
        // Sort events by start time
        const sortedEvents = dayEvents.sort(

        (a, b) => {
          const aStart = moment(a.start);
          const bStart = moment(b.start);

          // All-day events come first
          if (a.allDay && !b.allDay) return -1;
          if (!a.allDay && b.allDay) return 1;

          return aStart.valueOf() - bStart.valueOf();
        });

        agendaEntries.push({
          date: moment(currentMoment),
          events: sortedEvents,
          isToday: isToday(currentMoment),
          isCurrentMonth: currentMoment.isSame(currentDate, 'month')
        });
      }

      currentMoment.add(1, 'day');
    }

    return agendaEntries;
  }, [events, monthGrid, currentDate]);



  useEffect(() => {
    if (todayRef.current) {
      // Use a small timeout to ensure the DOM has fully rendered before scrolling
      const timeoutId = setTimeout(() => {
        todayRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [agendaData]);

  // Calculate event statistics
  const eventStats = useMemo(

  () => {
    const totalEvents = events.length;
    const confirmedEvents = events.filter((e) => e.confirmed).length;
    const unconfirmedEvents = events.filter((e) => !e.confirmed).length;
    const deadlines = events.filter((e) => e.type && e.type.includes('deadline')).length;
    const upcomingEvents = events.filter((e) => moment(e.start).isAfter(moment())).length;

    return {
      total: totalEvents,
      confirmed: confirmedEvents,
      unconfirmed: unconfirmedEvents,
      deadlines,
      upcoming: upcomingEvents
    };
  }, [events]);



  const isUserAssignee = (event) => {
    return !!(currentUser && Array.isArray(event.assignees) &&
    event.assignees.some((a) => a.attorney_name === currentUser.Name));
  };



  const getEventTypeBadge = (event) => {
    if (event.type === 'hard-deadline') {
      return <span className="event-badge deadline-hard">Hard Deadline</span>;
    } else if (event.type === 'soft-deadline') {
      return <span className="event-badge deadline-soft">Soft Deadline</span>;
    }
    return null;
  };



  const getUnconfirmedBadge = (event) => {
    if (!event.confirmed) {
      return <span className="event-badge unconfirmed">Unconfirmed</span>;
    }
    return null;
  };

  // Get assignee name
  const getAssigneeName =

  (event) => {
    if (event.assigneeObj) return event.assigneeObj;
    if (event.assignee_id && assignees) {
      const assignee = assignees.find((a) => a.id === event.assignee_id);
      return assignee ? assignee.name : 'Unknown';
    }
    return event.assignee || '';
  };



  const renderAssigneeAvatars = (event) => {
    // AI events get a robot emoji instead of avatars
    if (event.source === 'ai-calendar') {
      return (
        <div style={{
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
          marginRight: '8px'
        }}>
          <div
            title="AI-Detected Deadline"
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              flexShrink: 0
            }}>

            🤖
          </div>
        </div>);

    }

    if (!event.assignees || event.assignees.length === 0) {
      return null;
    }

    const renderAvatar =

    (assignee, index) => {
      const initials = assignee.attorney_name ?
      assignee.attorney_name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase() :
      '??';

      const avatarSize = '24px'; // Larger for agenda view
      const fontSize = '10px';

      const avatarStyle = {
        width: avatarSize,
        height: avatarSize,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.5)',
        objectFit: 'cover',
        flexShrink: 0
      };

      const initialsStyle = {
        ...avatarStyle,
        backgroundColor: 'rgba(100, 100, 100, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: fontSize,
        fontWeight: 'bold',
        color: 'var(--text-color)'
      };

      // Check if profile picture exists and is a valid URL
      const hasProfilePic = assignee.attorney_profile_pic &&
      typeof assignee.attorney_profile_pic === 'string' &&
      assignee.attorney_profile_pic.trim() !== '';

      if (hasProfilePic) {
        return (
          <img
            key={`avatar-${assignee.attorney_id}-${index}`}
            src={assignee.attorney_profile_pic}
            alt={assignee.attorney_name}
            title={assignee.attorney_name}
            style={avatarStyle}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={

            (e) => {
              // Gracefully fall back to initials if profile picture fails to load
              const parent = e.target.parentNode;
              const initialsDiv = document.createElement('div');
              initialsDiv.textContent = initials;
              initialsDiv.title = assignee.attorney_name;
              Object.assign(initialsDiv.style, initialsStyle);
              parent.replaceChild(initialsDiv, e.target);
            }} />);


      } else {
        return (
          <div
            key={`avatar-${assignee.attorney_id}-${index}`}
            title={assignee.attorney_name}
            style={initialsStyle}>

            {initials}
          </div>);

      }
    };

    return (
      <div style={{
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        marginRight: '8px'
      }}>
        {event.assignees.slice(0, 3).map((assignee, index) => renderAvatar(assignee, index))}
        {event.assignees.length > 3 &&
        <div
          title={event.assignees.slice(3).map((a) => a.attorney_name).join(', ')}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.5)',
            backgroundColor: 'rgba(100, 100, 100, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            flexShrink: 0,
            color: 'var(--text-color)'
          }}>

            +{event.assignees.length - 3}
          </div>}

      </div>);

  };

  // Format event time for agenda display
  const formatEventTime =

  (event) => {
    if (event.allDay) {
      return 'All day';
    }

    const start = moment(event.start);
    const end = moment(event.end);

    if (start.isSame(end, 'day')) {
      return `${formatTime(start)} - ${formatTime(end)}`;
    } else {
      return `${formatTime(start)} - ${formatDate(end, 'MMM D')} ${formatTime(end)}`;
    }
  };

  // Render month statistics
  const renderMonthStats =

  () => {
    return (
      <div className="agenda-stats">
        <h3>Month Overview</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-number">{eventStats.total}</span>
            <span className="stat-label">Total Events</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{eventStats.upcoming}</span>
            <span className="stat-label">Upcoming</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{eventStats.deadlines}</span>
            <span className="stat-label">Deadlines</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{eventStats.unconfirmed}</span>
            <span className="stat-label">Unconfirmed</span>
          </div>
        </div>
      </div>);

  };



  const getCategoryColorClass = (event) => {
    if (!event) return 'category-gray';

    // Use category_color if available
    if (event.category_color) {
      return `category-${event.category_color.toLowerCase()}`;
    }

    // Fallback: Map category names to colors
    if (event.category_name) {
      const categoryColorMap = {
        'Our Deadline': 'red',
        'Opponent Deadline': 'green',
        'Court/Arbitrator Deadline': 'purple',
        'Our Appearance Required': 'yellow',
        'Anything Else': 'blue',
        'AI Deadlines': 'blue'
      };

      const color = categoryColorMap[event.category_name] || 'gray';
      return `category-${color}`;
    }

    return 'category-gray';
  };

  // Render individual event item
  const renderEventItem =

  (event) => {
    const assigneeName = getAssigneeName(event);
    const isPastEvent = moment(event.end).isBefore(moment());
    const categoryColorClass = getCategoryColorClass(event);

    return (
      <div
        key={event.id}
        className={`agenda-event-item ${categoryColorClass} ${isPastEvent ? 'past-event' : ''} ${!event.confirmed ? 'unconfirmed' : ''}`}
        onClick={() => onEventClick && onEventClick(event)}>

        <div className="event-time-column">
          <span className="event-time">{formatEventTime(event)}</span>
          {getUnconfirmedBadge(event)}
          {/* Show avatars below the confirmed tag */}
          {renderAssigneeAvatars(event)}
        </div>
        
        <div className="event-details-column">
          <div className="event-title-row">
            <h4 className="event-title" title={event.title}>{event.title}</h4>
            {getEventTypeBadge(event)}
          </div>
          
          {event.description &&
          <p className="event-description" title={event.description}>{event.description}</p>}

          
          <div className="event-meta">
            {event.assignees && event.assignees.length > 0 &&
            <span className="event-assignee">
                <AppIcon icon="pi pi-user" /> {event.assignees.map((a) => a.attorney_name).join(', ')}
              </span>}

            {event.category_name &&
            <span className="event-category"><AppIcon icon="pi pi-folder" /> {event.category_name}</span>}

            {event.type &&
            <span className="event-type"><AppIcon icon="pi pi-tag" /> {event.type}</span>}

          </div>
        </div>
        
        <div className="event-actions-column">
          {onEventEdit && event.source !== 'ai-calendar' &&
          <button
            className="action-btn edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              onEventEdit(event);
            }}
            title="Edit event">

              <AppIcon icon="pi pi-pencil" />
            </button>}


          {onEventDelete && event.source !== 'ai-calendar' &&
          <button
            className="action-btn delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this event?')) {
                onEventDelete(event.id);
              }
            }}
            title="Delete event">

              <AppIcon icon="pi pi-trash" />
            </button>}

          
          {!event.confirmed && onConfirmResponsibility && isUserAssignee(event) &&
          <button
            className="action-btn confirm-btn"
            onClick={(e) => {
              e.stopPropagation();
              onConfirmResponsibility(event.id);
            }}
            title="Confirm responsibility">

              <AppIcon icon="pi pi-check" />
            </button>}

        </div>
      </div>);

  };



  const renderDaySection = (agendaEntry) => {
    const { date, events, isToday, isCurrentMonth } = agendaEntry;

    return (
      <div
        key={date.format('YYYY-MM-DD')}
        ref={isToday ? todayRef : null}
        className={`agenda-day-section ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}>

        <div className="day-header">
          <div className="day-info">
            <span className="day-name">{date.format('dddd')}</span>
            <span className="day-date">{date.format('MMMM D, YYYY')}</span>
          </div>
          <div className="event-count">
            {events.length} event{events.length !== 1 ? 's' : ''}
          </div>
        </div>
        
        <div className="day-events">
          {events.map((event) => renderEventItem(event))}
        </div>
      </div>);

  };

  // Render empty state
  const renderEmptyState =

  () => {
    return (
      <div className="agenda-empty-state">
        <div className="empty-icon"><AppIcon icon="pi pi-calendar" /></div>
        <h3>No events this month</h3>
        <p>There are no scheduled events for {currentDate.format('MMMM YYYY')}.</p>
      </div>);

  };

  // Render quick actions
  const renderQuickActions =

  () => {
    const today = moment();
    const thisWeek = events.filter((e) => {
      const eventStart = moment(e.start);
      return eventStart.isSame(today, 'week');
    });

    const nextWeek = events.filter((e) => {
      const eventStart = moment(e.start);
      const nextWeekStart = moment(today).add(1, 'week').startOf('week');
      return eventStart.isSame(nextWeekStart, 'week');
    });

    return (
      <div className="agenda-quick-actions">
        <h3>Quick Overview</h3>
        <div className="quick-stats">
          <div className="quick-stat">
            <span className="quick-number">{thisWeek.length}</span>
            <span className="quick-label">This Week</span>
          </div>
          <div className="quick-stat">
            <span className="quick-number">{nextWeek.length}</span>
            <span className="quick-label">Next Week</span>
          </div>
        </div>
      </div>);

  };

  return (
    <div className="agenda-view">
      <div className="agenda-header">
        <h2>Agenda for {currentDate.format('MMMM YYYY')}</h2>
      </div>
      
      <div className="agenda-sidebar">
        {renderMonthStats()}
        {renderQuickActions()}
      </div>
      
      <div className="agenda-content">
        {agendaData.length === 0 ?
        renderEmptyState() :

        <div className="agenda-days">
            {agendaData.map((agendaEntry) => renderDaySection(agendaEntry))}
          </div>}

      </div>
    </div>);

};

export default AgendaView;
